import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Users, Clock, Shield, CheckCircle } from 'lucide-react';

const TEAM_IMG = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/xmkdi5p0_IMG_0874.jpg";
const GENERAL_IMG = "https://images.unsplash.com/photo-1765021689980-a1ce75fdee3a?crop=entropy&cs=srgb&fm=jpg&w=800&q=80";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export const AboutPage = () => {
  return (
    <main className="bg-black pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-4 py-2 mb-6 inline-block">
                Our Story
              </span>
              <h1 className="text-5xl sm:text-6xl uppercase tracking-wider font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                About 23rd Environmental Units
              </h1>
              <p className="text-zinc-300 text-lg leading-relaxed mb-6">
                23rd Environmental Units LLC, doing business as 23rd Industries, was founded by Lee & Antwanette 
                with a vision to revolutionize industrial cleaning through cutting-edge dry ice blasting technology.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                Based in Haltom City, TX, we serve the entire DFW Metroplex and surrounding regions with 
                professional-grade cleaning solutions that minimize downtime, eliminate secondary waste, 
                and deliver exceptional results.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src={TEAM_IMG} 
                alt="23rd Environmental Units - Dry Ice Blasting in Action" 
                className="w-full aspect-[9/16] object-cover object-top"
              />
              <div className="absolute inset-0 border border-orange-500/30" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-black border-b border-zinc-800" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '15+', label: 'Years Experience' },
              { number: '100mi', label: 'Service Radius' },
              { number: '$1M+', label: 'Insurance Coverage' },
              { number: '24/7', label: 'Availability' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
                data-testid={`stat-${i}`}
              >
                <span className="stat-number">{stat.number}</span>
                <p className="text-zinc-400 text-sm uppercase tracking-widest font-mono mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-24 bg-zinc-950" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Our Mission
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-8">
                To provide industry-leading dry ice blasting services that help our clients maintain their 
                equipment and facilities with minimal disruption, maximum efficiency, and environmentally 
                responsible practices.
              </p>
              
              <div className="space-y-4">
                {[
                  'Deliver exceptional cleaning results every time',
                  'Minimize client downtime and operational disruption',
                  'Provide environmentally responsible solutions',
                  'Build long-term partnerships based on trust and results'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Our Values
              </h2>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: Award, title: 'Excellence', desc: 'Quality in every job' },
                  { icon: Users, title: 'Partnership', desc: 'Your success is ours' },
                  { icon: Clock, title: 'Reliability', desc: 'On time, every time' },
                  { icon: Shield, title: 'Integrity', desc: 'Honest & transparent' },
                ].map((value, i) => (
                  <div key={i} className="bg-black border border-zinc-800 p-6">
                    <value.icon className="w-8 h-8 text-orange-500 mb-4" />
                    <h3 className="text-lg uppercase tracking-wide font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {value.title}
                    </h3>
                    <p className="text-zinc-400 text-sm">{value.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-black border-t border-zinc-800" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2 mb-6 inline-block">
              Credentials
            </span>
            <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Certifications & Credentials
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Minority-Owned Business', desc: 'Certified minority-owned small business supporting supplier diversity programs' },
              { title: 'Woman-Owned Business', desc: 'Certified woman-owned enterprise meeting WOSB program requirements' },
              { title: 'Insurance Coverage', desc: 'Active General Liability coverage exceeding $1M for your peace of mind' },
              { title: 'SAM Registered', desc: 'Fully registered in the System for Award Management for government contracts' },
              { title: 'Industrial Experience', desc: '15+ years of hands-on mechanical and industrial operations experience' },
              { title: 'Mobile Capability', desc: 'Fully equipped mobile units for on-site service deployment' },
            ].map((cert, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="credential-card"
                data-testid={`certification-${i}`}
              >
                <h3 className="text-lg uppercase tracking-wide font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {cert.title}
                </h3>
                <p className="text-zinc-400 text-sm">{cert.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Performance */}
      <section className="py-24 bg-zinc-950" data-testid="experience-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={GENERAL_IMG} 
                alt="Industrial work experience" 
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2 mb-6 inline-block">
                Track Record
              </span>
              <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Past Performance
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-8">
                Our team brings over 15 years of combined experience in mechanical and industrial operations, 
                with proven success across diverse cleaning and restoration projects.
              </p>
              
              <div className="space-y-4">
                {[
                  'Heavy-duty diesel systems service & restoration',
                  'Industrial contamination removal',
                  'Field-based mechanical diagnostics & support',
                  'Surface restoration and metal finishing',
                  'Equipment preservation and maintenance'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-black border border-zinc-800">
                    <span className="w-8 h-8 bg-orange-600 text-black font-bold flex items-center justify-center text-sm flex-shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {i + 1}
                    </span>
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Ready to Partner With Us?
          </h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
            Join the growing list of businesses that trust 23rd Industries for their industrial 
            cleaning needs.
          </p>
          <Link to="/contact" className="btn-primary px-10 py-4 text-lg inline-flex items-center gap-2" data-testid="about-cta">
            Get In Touch
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};
